import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaEnvelope,
  FaEye,
  FaEyeSlash,
  FaLock,
} from "react-icons/fa";
import axios from "../../auth/auth";
import {
  capturePasswordResetToken,
  clearPasswordResetToken,
  getStoredPasswordResetToken,
} from "../../auth/passwordResetToken";
import logo from "../../utils/logo.png";

const cardClass =
  "w-full max-w-md rounded-3xl border border-slate-200 bg-white px-6 py-6 shadow-[0_20px_60px_rgba(15,23,42,0.12)] sm:px-10 sm:py-8";
const inputClass =
  "w-full rounded-xl border border-slate-200 bg-slate-50 py-3 pl-11 pr-4 text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/10";
export default function PasswordReset({ mode }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] =
    useState(false);
  const [resetToken, setResetToken] = useState(() => {
    return getStoredPasswordResetToken();
  });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const resetSubmittingRef = useRef(false);
  const isResetMode = mode === "reset";

  useEffect(() => {
    if (!isResetMode) {
      return undefined;
    }

    const captureResetToken = () => {
      const tokenFromLink = capturePasswordResetToken();
      if (tokenFromLink) {
        setResetToken(tokenFromLink);
      }
    };

    captureResetToken();
    window.addEventListener("hashchange", captureResetToken);
    return () => window.removeEventListener("hashchange", captureResetToken);
  }, [isResetMode]);

  const handleRequest = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");
    setIsSubmitting(true);

    try {
      const response = await axios.post("/auth/password-reset/request", {
        email,
      });
      setMessage(response.data.message);
    } catch (requestError) {
      setError(
        requestError.response?.data?.detail ||
          "We could not process the request. Please try again later.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = async (event) => {
    event.preventDefault();
    if (resetSubmittingRef.current) {
      return;
    }
    setError("");
    setMessage("");

    if (!resetToken) {
      setError("This password reset link is invalid or has expired.");
      return;
    }
    if (password !== passwordConfirmation) {
      setError("The passwords do not match.");
      return;
    }

    resetSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      const response = await axios.post("/auth/password-reset/confirm", {
        token: resetToken,
        new_password: password,
      });
      setMessage(response.data.message);
      setResetToken("");
      clearPasswordResetToken();
      setPassword("");
      setPasswordConfirmation("");
    } catch (requestError) {
      if (requestError.response?.status === 400) {
        setResetToken("");
        clearPasswordResetToken();
      }
      setError(
        requestError.response?.data?.detail ||
          "This password reset link is invalid or has expired.",
      );
    } finally {
      resetSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleSubmit = isResetMode ? handleReset : handleRequest;

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4 py-6 sm:px-6">
      <main className={cardClass}>
        <div className="mb-7 flex justify-center">
          <img src={logo} alt="Park Engage" className="h-auto w-48" />
        </div>

        <div className="mb-6 text-center">
          <h1 className="text-xl font-bold tracking-tight text-slate-900">
            {isResetMode ? "Reset your password" : "Forgot your password?"}
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            {isResetMode
              ? "Choose a new password with at least 12 characters."
                : "Enter your account email and we will send password reset instructions."}
          </p>
        </div>

        {message ? (
          <p
            role="status"
            className="rounded-xl border border-green-100 bg-green-50 px-4 py-3 text-sm font-medium text-green-700"
          >
            {message}
          </p>
        ) : isResetMode && !resetToken ? (
          <p className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-600">
            This password reset link is invalid or has expired.
          </p>
        ) : (
          <form className="space-y-5" onSubmit={handleSubmit}>
            {!isResetMode ? (
              <div>
                <label
                  className="mb-2 block text-sm font-semibold text-slate-700"
                  htmlFor="reset-email"
                >
                  Email address
                </label>
                <div className="relative">
                  <FaEnvelope className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    id="reset-email"
                    type="email"
                    autoComplete="email"
                    maxLength={254}
                    placeholder="Enter your email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                    className={inputClass}
                  />
                </div>
              </div>
            ) : (
              <>
                <div>
                  <label
                    className="mb-2 block text-sm font-semibold text-slate-700"
                    htmlFor="new-password"
                  >
                    New password
                  </label>
                  <div className="relative">
                    <FaLock className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      id="new-password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      minLength={12}
                      maxLength={128}
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      required
                      className={`${inputClass} pr-12`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((visible) => !visible)}
                      className="absolute inset-y-0 right-3 flex w-9 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-200 hover:text-slate-700"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
                <div>
                  <label
                    className="mb-2 block text-sm font-semibold text-slate-700"
                    htmlFor="confirm-password"
                  >
                    Confirm new password
                  </label>
                  <div className="relative">
                    <FaLock className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      id="confirm-password"
                      type={showPasswordConfirmation ? "text" : "password"}
                      autoComplete="new-password"
                      minLength={12}
                      maxLength={128}
                      value={passwordConfirmation}
                      onChange={(event) =>
                        setPasswordConfirmation(event.target.value)
                      }
                      required
                      className={`${inputClass} pr-12`}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowPasswordConfirmation((visible) => !visible)
                      }
                      className="absolute inset-y-0 right-3 flex w-9 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-200 hover:text-slate-700"
                      aria-label={
                        showPasswordConfirmation
                          ? "Hide confirmed password"
                          : "Show confirmed password"
                      }
                    >
                      {showPasswordConfirmation ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
              </>
            )}

            {error && (
              <p
                role="alert"
                className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-600"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="flex w-full items-center justify-center gap-3 rounded-xl bg-blue-600 py-3 font-semibold text-white shadow-lg shadow-blue-600/20 transition hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-500/20 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting
                ? "Please wait..."
                : isResetMode
                  ? "Update password"
                  : "Send reset instructions"}
              {!isSubmitting && <FaArrowRight className="text-sm" />}
            </button>
          </form>
        )}

        <div className="mt-6 text-center">
          <Link
            to="/"
            className="text-sm font-medium text-blue-600 hover:text-blue-700"
          >
            Back to sign in
          </Link>
        </div>
      </main>
    </div>
  );
}
